import type { LocalPaymentData } from "../helpers";
import { PaymentMethod, MethodId, CardBrand } from "../enums";

export interface LocalPaymentSchemaDerived {
  paymentMethods: string[];
  creditBrands: string[];
  debitBrands: string[];
}

export interface PaymentMethodType {
  id: string;
  order: number;
  description: string;
  enabled: boolean;
  asksFor: string;
  brands: Array<{
    id: string;
    description: string;
    enabled: boolean;
    imageUrl: string;
  }>;
}

const FALLBACK_METHOD_ORDER: string[] = [
  PaymentMethod.Credit,
  PaymentMethod.Debit,
  PaymentMethod.Pix,
  PaymentMethod.Cash,
];

const METHOD_DISPLAY_LABEL: Record<string, string> = {
  [PaymentMethod.Credit]: "Cartão de Crédito",
  [PaymentMethod.Debit]: "Cartão de Débito",
  [PaymentMethod.Pix]: "Pix",
  [PaymentMethod.Cash]: "Dinheiro",
};

const FALLBACK_CREDIT_BRANDS: string[] = [
  CardBrand.Mastercard,
  CardBrand.Visa,
  CardBrand.Hipercard,
  CardBrand.Elo,
  CardBrand.Amex,
];

const FALLBACK_DEBIT_BRANDS: string[] = [...FALLBACK_CREDIT_BRANDS];

function readItemsEnum(node: unknown): string[] {
  if (!node || typeof node !== "object") {
    return [];
  }
  const { items } = node as { items?: { enum?: unknown } };
  const { enum: rawEnum } = (items ?? {}) as { enum?: unknown };
  if (!Array.isArray(rawEnum)) {
    return [];
  }
  return rawEnum.filter((item): item is string => typeof item === "string");
}

export function parseLocalPaymentProviderSchema(
  schemaRaw: string | undefined | null,
): LocalPaymentSchemaDerived | null {
  if (!schemaRaw || typeof schemaRaw !== "string") {
    return null;
  }
  let root: unknown;
  try {
    root = JSON.parse(schemaRaw);
  } catch {
    return null;
  }
  if (!root || typeof root !== "object") {
    return null;
  }
  const props = (root as { properties?: Record<string, unknown> }).properties;
  if (!props || typeof props !== "object") {
    return null;
  }

  const paymentMethods = readItemsEnum(props.methods);
  if (paymentMethods.length === 0) {
    return null;
  }

  const acquirer = props.acquirer as
    | { properties?: Record<string, unknown> }
    | undefined;
  const acquirerProps = acquirer?.properties;

  const creditBrands = readItemsEnum(acquirerProps?.creditBrands);
  const debitBrands = readItemsEnum(acquirerProps?.debitBrands);

  return {
    paymentMethods,
    creditBrands,
    debitBrands,
  };
}

function apiMethodToMethodId(apiValue: string): string {
  const trimmed = apiValue.trim();
  if (trimmed === PaymentMethod.Cash) {
    return MethodId.Dinheiro;
  }
  return trimmed.toLowerCase().replace(/\s+/g, "_");
}

function buildBrandsRow(
  schemaBrands: string[],
  enabledBrands: string[],
): PaymentMethodType["brands"] {
  return schemaBrands.map((brand) => ({
    id: brand.toLowerCase(),
    description: brand,
    enabled: enabledBrands.some((b) => b.toLowerCase() === brand.toLowerCase()),
    imageUrl: "",
  }));
}

function resolveMethodOrder(
  derived: LocalPaymentSchemaDerived | null,
): string[] {
  if (derived?.paymentMethods?.length) {
    return derived.paymentMethods;
  }
  return FALLBACK_METHOD_ORDER;
}

function resolveBrandOptions(
  derived: LocalPaymentSchemaDerived | null,
  cardKind: "credit" | "debit",
): string[] {
  if (!derived) {
    return cardKind === "credit"
      ? FALLBACK_CREDIT_BRANDS
      : FALLBACK_DEBIT_BRANDS;
  }
  const fromSchema =
    cardKind === "credit" ? derived.creditBrands : derived.debitBrands;
  return Array.isArray(fromSchema) ? fromSchema : [];
}

export function buildPaymentMethodsFromSchema(
  localPaymentData: LocalPaymentData | null,
  derived: LocalPaymentSchemaDerived | null,
): PaymentMethodType[] {
  const methodKeys = resolveMethodOrder(derived);
  const enabledMethods = localPaymentData?.methods || [];

  const creditBrandOptions = resolveBrandOptions(derived, "credit");
  const debitBrandOptions = resolveBrandOptions(derived, "debit");

  return methodKeys.map((apiMethod, index) => {
    const methodId = apiMethodToMethodId(apiMethod);
    const trimmed = apiMethod.trim();
    const isEnabled = enabledMethods.some(
      (e) => e.toLowerCase() === trimmed.toLowerCase(),
    );

    let brands: PaymentMethodType["brands"] = [];
    if (trimmed === PaymentMethod.Credit) {
      brands = buildBrandsRow(
        creditBrandOptions,
        isEnabled ? localPaymentData?.creditBrands || [] : [],
      );
    } else if (trimmed === PaymentMethod.Debit) {
      brands = buildBrandsRow(
        debitBrandOptions,
        isEnabled ? localPaymentData?.debitBrands || [] : [],
      );
    }

    const description =
      METHOD_DISPLAY_LABEL[trimmed] ??
      METHOD_DISPLAY_LABEL[apiMethod] ??
      apiMethod;

    return {
      id: methodId,
      order: index + 1,
      description,
      enabled: isEnabled,
      asksFor: "",
      brands,
    };
  });
}
